import { email, uid } from '@/firebase'
import { AuthorizedService } from '@/helpers/fs/users/authorizedService'
import { Calendar } from '@/helpers/fs/users/authorizedServices/accounts/calendar'
import { AuthorizedServiceAccount } from '@/helpers/fs/users/authorizedServices/authorizedServiceAccount'
import {
  FSAuthorizedServiceAccount,
  FSCalendar,
  OauthServiceName
} from '@WombatTechnology/nitte-time-lib'
import { flatten } from 'es-toolkit/compat'
import { getDoc, getDocs, onSnapshot } from 'firebase/firestore'
import { defineStore } from 'pinia'

export type AccountWithCalendars = {
  account: FSAuthorizedServiceAccount
  calendars: FSCalendar[]
}

// カレンダーのアカウントを管理するStore
type State = {
  googleAccounts: AccountWithCalendars[]
  msAccounts: AccountWithCalendars[]
}

export const useCalendarAccountsStore = defineStore('calendarAccounts', {
  state: (): State => {
    return {
      googleAccounts: [],
      msAccounts: []
    }
  },
  actions: {
    async setup() {
      const services = []

      // NOTE: 本番だと連携していないcollectionにsnapshotかけるとエラーになるので、連携しているサービスのみ取得する
      const [googleDoc, msDoc] = await Promise.all([
        getDoc(AuthorizedService.doc(uid()!, 'google')),
        getDoc(AuthorizedService.doc(uid()!, 'ms'))
      ])
      if (googleDoc.exists()) {
        services.push('google')
      }
      if (msDoc.exists()) {
        services.push('ms')
      }

      services.map((calendarType) => {
        onSnapshot(
          AuthorizedServiceAccount.collection(
            uid()!,
            calendarType as OauthServiceName
          ),
          async (collection) => {
            const accounts = await Promise.all(
              collection.docs.map(async (d) => {
                const account = await getDoc(d.ref)
                const accountData = account.data()!
                const calendars = await getDocs(
                  Calendar.collection(
                    uid()!,
                    calendarType as OauthServiceName,
                    accountData.email
                  )
                )
                return {
                  account: accountData,
                  calendars: calendars.docs
                    .map((d) => d.data())
                    .sort((c) => (c.primary ? -1 : 1))
                }
              })
            )
            if (calendarType === 'google') {
              this.$patch({ googleAccounts: accounts })
            } else {
              this.$patch({ msAccounts: accounts })
            }
          }
        )
      })
    },
    findAccountIdOfCalendarId(calendarId: string) {
      return this.calendars.find((c) => c.id === calendarId)?.accountId
    },
    isLastActiveCalendar(calendarId: string) {
      return (
        this.activeCalendars.length === 1 &&
        this.activeCalendars[0].id === calendarId
      )
    }
  },
  getters: {
    // メインのカレンダーアカウント
    mainCalendarAccount(state): AccountWithCalendars {
      return this.accounts.find((ac) => ac.account.email === email()!)!
    },
    // サブのカレンダーアカウント
    subCalendarAccounts(state): AccountWithCalendars[] {
      return this.accounts.filter((ac) => ac.account.email !== email()!)
    },
    accounts(state): AccountWithCalendars[] {
      return state.googleAccounts.concat(this.msAccounts)
    },
    // 全てのカレンダー
    calendars(state): FSCalendar[] {
      return flatten(this.accounts.map((ca) => ca.calendars))
    },
    // メインカレンダー
    mainCalendar(): FSCalendar {
      // メルアドと同じカレンダーを優先
      const userEmailCalendar = this.calendars.find((c) => c.id === email())
      if (userEmailCalendar) return userEmailCalendar

      // ない場合は最初のprimaryなカレンダー
      return this.calendars.find((c) => c.primary)!
    },
    // アクティブなカレンダー
    activeCalendars(): FSCalendar[] {
      return this.calendars.filter((c) => c.active)
    },
    // 会議室のカレンダー
    roomCalendars(): FSCalendar[] {
      return this.calendars.filter((c) => c.type === 'room')
    },
    // 他のカレンダー
    otherCalendars(): FSCalendar[] {
      return this.calendars.filter((c) => c.type === 'other')
    },
    hasMultiActiveCalendars(state): boolean {
      return this.activeCalendars.length > 1
    }
  }
})
